import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import {
  Alert, Badge, Button, Checkbox, CollapsibleSection, Combobox, Counter, DateRangePicker, EmptyState, FilterChip,
  IconGuide, IconInfoCircle, IconWarningCircle, Modal, Search, SidePanel, Skeleton, StatusHint,
  type ComboboxOption, type DateRangeValue,
} from '@indipia/ui-core'
import type { NavFn } from '../pages/SchedulerPage'
import { StepIndicator } from './SiteSelectView'
import { useCommonCodes } from '../context/CommonCodesContext'
import { useHolidayRange } from '../hooks/useHolidays'
import { useScheduleSave, type OriginType, type SavePayload } from '../hooks/useScheduleSave'
import { ScheduleReplaceModal } from '../components/ScheduleReplaceModal'
import { Temp_SourceViewer } from '../components/Temp_SourceViewer'
import { buildRows, suggestWorkTypes } from './rawMatch'
import { countWorkdays, formatISO, isHolidayCovered } from './scheduleUtils'
import type { ExtractWork } from './extractTypes'

// sv-schedule POST /schedule/estimate-analyses/raw 응답(data) 형태
export type RawWork = {
  rawText:     string
  matchedCode: string | null
  matchedName: string | null
  standard:    boolean
  duration:    number | null
  color:       string | null
  /** 견적서 원문 순번(없으면 표준 → 미매칭 순으로 나열) */
  seq?:        number | null
  /** 서버가 계산한 공종 후보(최대 2개, 미매칭 항목용). 필드가 없는 응답이면 화면에서 임시로 계산한다 */
  candidates?: { workTypeCode: string; workTypeName: string }[] | null
  /** 상세내역에서 찾았거나 세분화로 만든 항목이 속한 묶음(대분류) 이름 — 요약표 항목은 없음 */
  group?:      string | null
  /** 그 공종의 세부 공사 내용 요약(원문상세 컬럼용) — 요약표에 있으면 그걸, 없으면 상세내역에서 간추림 */
  detail?:     string | null
}
export type RawExtract = {
  standardWorks:    RawWork[]
  nonStandardWorks: RawWork[]
  source:           string
  rawAnswer:        string | null
  // 분석 결과 구분 — SUCCESS | NO_WORKS | FALLBACK_KEYWORD | FAILED (서버가 내려주지 않으면 SUCCESS 로 본다)
  analysisStatus?: string | null
  // 실패·폴백 사유 — LLM_UNAVAILABLE | LLM_PARSE_FAILED
  failureReason?:  string | null
  // 분석에 사용한 엑셀 시트 이름(시트를 지정한 경우)
  sheetName?:      string | null
}

/** 사용자가 이 화면에서 고른 값 — 이전·다음 왕복 시 유지하려고 부모가 들고 있는다 */
export type RawChoice = {
  /** 견적서 행 key(자동 연결 s0…, 미매칭 n0…) → 사용자가 고른 공종 코드. 미매칭 행은 연결, 자동 연결 행은 AI 연결을 덮어쓴다 */
  picks:     Record<string, string>
  /** 견적서에 없어 직접 추가한 공종 코드 */
  added:     string[]
  /** 체크를 해제한 공통 항목(스케줄 전용) 코드 */
  commonOff: string[]
  /** '시공포함' 체크를 해제한 견적서 행 key — 공종이 있는 행은 기본 체크(포함). 공종이 아직 없는(확인 필요) 행은 체크되지 않은 채 시작하고 담기지 않는다 */
  off:       string[]
  /** 공종코드 → 사용자가 조정한 소요기간(일). 없으면 추출값 > 설정의 기본 소요일 > 1 */
  durations: Record<string, number>
  /** 공사 기간 — 기간 선택기는 첫 클릭에 {start, end: null} 을 주므로 중간 상태까지 그대로 들고 있는다 */
  period:    DateRangeValue
  /** 이번에 바꾼 소요일을 앞으로의 기본 소요일로 저장 — 기본 해제 */
  saveDefaults: boolean
}
export const EMPTY_RAW_CHOICE: RawChoice = {
  picks: {}, added: [], commonOff: [], off: [], durations: {}, period: { start: null, end: null },
  saveDefaults: false,
}

type Props = {
  navigate: NavFn
  raw?: RawExtract | null
  choice: RawChoice
  onChoice: (c: RawChoice) => void
  /** 1단계(현장 선택)에서 고른 현장 id */
  selectedSiteId?: number
  /** 초기화(재생성) 흐름 전용 — 이미 스케줄이 있는 현장을 알고 시작하는 경우. 지정되면 확인 없이 강제 교체(force=true)한다 */
  resetSiteId?: number
  /** 생성 경로 — 진입 시점에 확정되어 흐름 끝까지 전달된다(QUOTE 견적서 자동 생성). 마지막 단계에서 추정하지 않는다 */
  originType?: OriginType | null
  /** 업로드한 견적서 원본 파일 — 있으면 '원문 보기'로 공종표 옆에서 볼 수 있다(샘플·새로고침 등으로 없으면 버튼을 숨긴다) */
  sourceFile?: File | null
  /** 스케줄 생성 완료 — 현장 목록 갱신·간트 이동은 부모가 처리. 미지정 시 navigate('gantt', scheduleId) 로 폴백 */
  onCreated?: (scheduleId: number) => void
}

type RowFilter = 'all' | 'open' | 'skip'
// 추천 공종은 항상 이 개수까지만 보여 준다(서버 후보든 화면 계산이든 같은 상한) — 드롭다운 '추천' 표시와 추천 버튼이 같은 목록을 쓴다
const MAX_CANDIDATES = 2

const WARN_BG   = { background: 'var(--color-warningSubtle)' } as const
const WARN_EDGE = { boxShadow: 'inset 3px 0 0 var(--color-warning)' } as const

/**
 * 견적서 추출 결과 확인 — 위에서 공사 기간과 공종 합계를 비교하고, 아래 견적 항목 확인서에서 견적서에 적은 내용과 스케줄 공종을 나란히 보여 준다.
 * 모든 행의 스케줄 공종은 드롭다운이라 바로 바꿀 수 있고, 연결 못 한 항목(확인 필요)은 추천 공종을 누르거나 드롭다운에서 골라 확인을 끝낸다(고르면 시공포함이 자동으로 체크된다). 체크된 공종만 스케줄로 만든다.
 * 견적서에 없는 공종은 '공종 추가'에서 칩으로 고른다. 이 화면이 자동 생성(QUOTE) 흐름의 마지막 입력 단계라서
 * 공사 기간을 고른 뒤 [스케줄 생성하기]로 바로 스케줄을 만들고 간트로 넘어간다(자동 배정 켬이면 서버가 저장하면서 파트너를 배정한다).
 */
export function ExtractRawView2({ navigate, raw, choice, onChoice, selectedSiteId, resetSiteId, originType, sourceFile, onCreated }: Props) {
  const user = useAuthStore(s => s.user)
  const { codes } = useCommonCodes()
  const workTypes = useMemo(
    () => [...(codes?.workTypes ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [codes?.workTypes],
  )
  const byCode = useMemo(() => new Map(workTypes.map(w => [w.workTypeCode, w])), [workTypes])
  const commons = useMemo(() => workTypes.filter(w => w.scheduleOnlyYn === 'Y'), [workTypes])
  const pickable = useMemo(() => workTypes.filter(w => w.scheduleOnlyYn !== 'Y'), [workTypes])

  const rows = useMemo(() => (raw ? buildRows(raw) : []), [raw])
  const matchedCodes = useMemo(
    () => new Set((raw?.standardWorks ?? []).map(w => w.matchedCode).filter((c): c is string => !!c)),
    [raw],
  )

  const [filter, setFilter] = useState<RowFilter>('all')
  const [addOpen, setAddOpen] = useState(false)
  const [addQuery, setAddQuery] = useState('')
  const [commonsOpen, setCommonsOpen] = useState(false)
  const [sourceOpen, setSourceOpen] = useState(false)
  const tableRef = useRef<HTMLTableElement>(null)

  // 공종별 기본 소요일(설정 화면 저장값, 없으면 마스터 표준값) — 소요기간을 직접 정하지 않은 공종(직접 고른·추가한 공종, 공통 항목)의 표시·전달 기본값.
  // 다음 단계(공종·공사일)가 같은 값으로 시작하므로 이 화면의 일수와 어긋나지 않는다.
  const headers = useMemo(() => ({
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId) }   : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }), [user?.compId, user?.memberId])
  const [defaultDurations, setDefaultDurations] = useState<Map<string, number>>(new Map())
  useEffect(() => {
    apiClient.get<{ data: { workTypeDurations: { workTypeCode: string; duration: number }[] } }>('/schedule/settings', { headers })
      .then(res => setDefaultDurations(new Map((res.data?.data?.workTypeDurations ?? []).map(d => [d.workTypeCode, d.duration]))))
      .catch(() => {})
  }, [headers])

  const targetSiteId = resetSiteId ?? selectedSiteId ?? null
  // 저장 공용 로직(기존 스케줄 교체 확인 · 오류 안내 · 기본 소요일 저장)
  const { saving: busy, replaceOpen, replaceScheduleId, save, confirmReplace, cancelReplace } = useScheduleSave(scheduleId => {
    if (onCreated) onCreated(scheduleId)
    else navigate('gantt', scheduleId)
  })
  const [showOverrunConfirm, setShowOverrunConfirm] = useState(false)

  // 공사 기간 총 일수 = 작업일 수(토·일요일·공휴일 제외, 양끝 포함). 공휴일 조회 상태·범위를 확인하기 전에는 계산하지 않는다(A-08).
  // 조회 범위는 올해 기준 전후 1년에 선택한 기간이 벗어나는 만큼만 넓힌다 — 달력에서 고르는 도중 매번 다시 조회하지 않게.
  const { period } = choice
  const thisYear = new Date().getFullYear()
  const holidayState = useHolidayRange(
    `${Math.min(thisYear, period.start?.getFullYear() ?? thisYear) - 1}-01-01`,
    `${Math.max(thisYear, (period.end ?? period.start)?.getFullYear() ?? thisYear) + 1}-12-31`,
  )
  const periodDays: number | null = (period.start && period.end && holidayState.status === 'success' && holidayState.from && holidayState.to
    && isHolidayCovered({ from: holidayState.from, to: holidayState.to }, period.start, period.end))
    ? countWorkdays(period.start, period.end, holidayState.dates)
    : null

  // 견적서 행에 공종을 연결(code)한다. 미매칭 행은 연결, 자동 연결 행은 AI 연결을 덮어쓴다.
  // null 이면 사용자 선택을 풀어 되돌린다 — 자동 연결 행은 AI 연결로, 미매칭 행은 다시 '확인 필요'로.
  // 시공포함을 해제한 행은 드롭다운이 잠겨 있어 여기로 오지 않는다
  const setPick = (key: string, code: string | null) => {
    const picks = { ...choice.picks }
    if (code) picks[key] = code
    else delete picks[key]
    onChoice({ ...choice, picks })
  }
  // 직접 추가 — 칩을 누를 때마다 넣고 뺀다
  const toggleAdded = (code: string) => {
    const added = choice.added.includes(code) ? choice.added.filter(c => c !== code) : [...choice.added, code]
    onChoice({ ...choice, added })
  }
  const toggleCommon = (code: string, on: boolean) => {
    const off = new Set(choice.commonOff)
    if (on) off.delete(code)
    else off.add(code)
    onChoice({ ...choice, commonOff: [...off] })
  }
  const toggleOff = (key: string, on: boolean) => {
    const off = new Set(choice.off)
    if (on) off.delete(key)
    else off.add(key)
    onChoice({ ...choice, off: [...off] })
  }
  const setDuration = (code: string, v: number) => {
    onChoice({ ...choice, durations: { ...choice.durations, [code]: v } })
  }

  // 그 행에 연결된 공종코드(사용자가 고른 코드 > AI 매칭 코드). 없으면 null
  const codeOf = (r: typeof rows[number]): string | null => choice.picks[r.key] ?? (r.matched ? (r.work.matchedCode ?? null) : null)
  // 포함 여부 — 공종이 연결돼 있고 포함-해제(off)에 없으면 포함
  const isIncluded = (r: typeof rows[number]): boolean => !!codeOf(r) && !choice.off.includes(r.key)
  // 표시·전달용 소요기간 — 사용자가 조정한 값 > 추출값 > 설정의 기본 소요일 > 1
  const durationOf = (code: string, fallback?: number | null): number => choice.durations[code] ?? fallback ?? defaultDurations.get(code) ?? 1
  // 견적서에서 추출한 소요일은 AI 가 연결한 공종에만 해당한다 — 사용자가 다른 공종으로 바꾸면 쓰지 않는다
  const extractedDuration = (r: typeof rows[number], code: string | null): number | null => (code && code === r.work.matchedCode ? r.work.duration : null)

  // 시공포함 해제(= 제외) — 공종이 있는 행에서 체크를 해제하면 이번 스케줄에 넣지 않고 드롭다운·일수도 잠근다.
  // 공종이 아직 없는 행은 해제할 것도 없이 처음부터 담기지 않는다(체크 안 된 채 시작, 공종을 고르면 자동으로 체크)
  const isExcluded = (r: typeof rows[number]): boolean => !!codeOf(r) && choice.off.includes(r.key)
  // 행에 공종이 연결된 경위 — matched(AI 연결) · picked(직접 선택) · open(연결된 공종 없음)
  const stateOf = (r: typeof rows[number]): 'matched' | 'picked' | 'open' =>
    choice.picks[r.key] ? 'picked' : r.matched ? 'matched' : 'open'
  // 확인 필요 — 공종이 아직 없는 행(공종을 고를 때까지 계속 확인 필요)
  const needsAction = (r: typeof rows[number]): boolean => stateOf(r) === 'open'

  const itemRows = rows.filter(r => !r.header)
  const unmatchedRows = rows.filter(r => !r.matched && !r.header)
  const unresolved = unmatchedRows.filter(needsAction).length
  const excludedCount = itemRows.filter(isExcluded).length
  const shownRows = filter === 'all'
    ? rows
    : rows.filter(r => !r.header && (filter === 'open' ? needsAction(r) : isExcluded(r)))
  const checkedCommons = commons.filter(c => !choice.commonOff.includes(c.workTypeCode))

  // 이번 스케줄에 들어갈 공종 — 자동 연결·직접 선택(포함 체크) + 직접 추가 + 체크한 공통 항목.
  // 소요기간은 공종코드 기준이라 같은 공종이 여러 행에 연결돼도 한 번만 담는다(합계·저장 값이 같은 목록을 쓴다).
  // baselineOf 는 사용자가 바꾸기 전의 소요일(추출값 > 설정 기본값 > 1) — '바꾼 소요일' 판정 기준
  const { selectedWorks, baselineOf } = (() => {
    const out = new Map<string, ExtractWork>()
    const base = new Map<string, number>()
    const put = (code: string | null | undefined, name: string, fallback?: number | null) => {
      if (!code || out.has(code)) return
      out.set(code, { workTypeCode: code, workTypeName: name, duration: durationOf(code, fallback) })
      base.set(code, fallback ?? defaultDurations.get(code) ?? 1)
    }
    itemRows.filter(isIncluded).forEach(r => {
      const code = codeOf(r)
      put(code, byCode.get(code ?? '')?.workTypeName ?? r.work.matchedName ?? '', extractedDuration(r, code))
    })
    choice.added.forEach(code => put(code, byCode.get(code)?.workTypeName ?? ''))
    checkedCommons.forEach(c => put(c.workTypeCode, c.workTypeName))
    return { selectedWorks: [...out.values()], baselineOf: base }
  })()
  const totalWorkDays = selectedWorks.reduce((sum, w) => sum + (w.duration ?? 1), 0)
  const saveWorks = selectedWorks.map(w => ({ workTypeCode: w.workTypeCode, duration: w.duration ?? 1 }))
  // 이번에 바꾼 소요일(선택한 공종 중 초기값과 달라진 것) — 기본 소요일 저장 선택 노출·저장 대상
  const changedDurations = saveWorks
    .filter(w => choice.durations[w.workTypeCode] != null && choice.durations[w.workTypeCode] !== (baselineOf.get(w.workTypeCode) ?? 1))
    .map(w => ({ workTypeCode: w.workTypeCode, duration: choice.durations[w.workTypeCode] }))
  const commonCodes = new Set(commons.map(c => c.workTypeCode))
  // 추출 표준 공종 수 — AI 가 견적서에서 찾아 표준 공종에 연결한 서로 다른 공종의 개수(사용자가 바꿔도 변하지 않는다)
  const extractedCount = matchedCodes.size
  const commonDays = selectedWorks.filter(w => commonCodes.has(w.workTypeCode)).reduce((sum, w) => sum + (w.duration ?? 1), 0)

  // 같은 공종이 포함된 행 수 — 직접 추가 칩의 중복 선택을 막고, 이미 포함된 공종에 연결한 행에 안내를 붙인다
  const codeUse = new Map<string, number>()
  const bumpUse = (code: string) => codeUse.set(code, (codeUse.get(code) ?? 0) + 1)
  itemRows.filter(isIncluded).forEach(r => bumpUse(codeOf(r) as string))
  choice.added.forEach(bumpUse)
  checkedCommons.forEach(c => bumpUse(c.workTypeCode))
  const usedByOther = (code: string, own: boolean) => (codeUse.get(code) ?? 0) - (own ? 1 : 0) > 0
  // 같은 공종이 여러 행에 포함되면 맨 위 행에만 일수 입력을 둔다 — 소요기간은 공종코드 기준으로 공유돼 입력이 둘이면 어느 쪽이 값을 쥐는지 헷갈린다.
  // 순서는 화면 순서(견적서 행 → 직접 추가). 아래 행은 공사일수 칸에 '위 행과 함께'만 보여 준다
  const durationOwner = new Map<string, string>()
  itemRows.filter(isIncluded).forEach(r => { const c = codeOf(r) as string; if (!durationOwner.has(c)) durationOwner.set(c, r.key) })
  choice.added.forEach(c => { if (!durationOwner.has(c)) durationOwner.set(c, `add:${c}`) })

  // 공사 기간 대비 공종 합계 — 작업일 기준 단순 합산. 위에서 알리고, 초과한 채 생성하면 한 번 더 확인한다(막지는 않는다)
  const over = periodDays != null ? totalWorkDays - periodDays : null

  // 스케줄 생성 조건 — 현장·공종·공사 기간(일수까지 계산됨)이 모두 있어야 한다
  const canCreate = targetSiteId != null && selectedWorks.length > 0 && !!period.start && !!period.end && !!periodDays
  const createBlockedReason = selectedWorks.length === 0
    ? '스케줄에 넣을 공종을 하나 이상 골라 주세요'
    : !period.start || !period.end
      ? '공사 기간을 고르면 스케줄을 만들 수 있어요'
      : '공사 기간 일수를 확인하면 스케줄을 만들 수 있어요'

  // 생성 경로는 진입 시점에 확정된 값을 그대로 쓴다
  const buildPayload = (): SavePayload => ({
    flow: 'auto',
    siteId:     targetSiteId as number,
    startDate:  formatISO(period.start as Date),
    endDate:    formatISO(period.end as Date),
    works:      saveWorks,
    // 이 화면에는 자동매칭 스위치가 없다 — 기존 기본값(켬) 그대로 서버가 저장하면서 파트너를 자동 배정한다
    autoAssign: true,
    originType: originType as OriginType,
    // resetSiteId(초기화 흐름)는 이미 "기존 스케줄 교체"를 사용자가 확인한 뒤 들어온 것이므로 바로 force=true.
    // 그 외는 force=false로 시도하고, 서버가 SCHEDULE_ALREADY_EXISTS 를 돌려주면 교체 확인창을 띄운다.
    force:      !!resetSiteId,
    defaultDurations: choice.saveDefaults ? changedDurations : [],
  })

  // 배정 확인 화면을 거치지 않고 바로 저장한다 — 자동 배정 켬이면 서버가 저장하면서 파트너를 배정하고, 저장되면 부모가 간트로 보낸다
  const doSubmit = async () => {
    if (targetSiteId == null) return
    if (!originType) { toast.error('스케줄 생성 경로를 확인할 수 없어요. 처음부터 다시 시도해 주세요'); return }
    await save(buildPayload())
  }

  // [스케줄 생성하기] — 공종 합계가 공사 기간보다 길면 한 번 확인한 뒤 진행한다
  const handleCreate = () => {
    if (!canCreate || busy) return
    if (over != null && over > 0) { setShowOverrunConfirm(true); return }
    void doSubmit()
  }

  // 다음 '확인 필요' 행으로 스크롤 — 화면 아래쪽에 있는 첫 미해결 행, 없으면 처음 행으로 돌아간다
  const jumpToNextOpen = () => {
    const els = Array.from(tableRef.current?.querySelectorAll<HTMLElement>('tr[data-open="true"]') ?? [])
    if (els.length === 0) return
    const next = els.find(el => el.getBoundingClientRect().top > window.innerHeight * 0.6) ?? els[0]
    // 모션 줄이기 설정이면 부드러운 스크롤 없이 바로 이동한다(D-07 §19.4)
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    next.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' })
  }

  const closeAdd = () => { setAddOpen(false); setAddQuery('') }
  const addList = pickable.filter(w => !addQuery.trim() || w.workTypeName.toLowerCase().includes(addQuery.trim().toLowerCase()))

  // 드롭다운 목록 — 공통 항목은 뺀 공종 전체(넣지 않음은 목록이 아니라 시공포함 체크 해제로 관리). 행마다 새로 만들지 않게 한 번만 만든다
  const pickOptions = useMemo<ComboboxOption[]>(() => pickable.map(w => ({ value: w.workTypeCode, label: w.workTypeName })), [pickable])

  // 미매칭 행의 추천 공종 — 서버 후보가 내려오면(빈 배열 포함) 그것만 쓴다. 필드가 없을 때만 화면에서 임시 계산. 어느 쪽이든 MAX_CANDIDATES 까지만
  const candidatesOf = (r: typeof rows[number]) =>
    (r.work.candidates ?? suggestWorkTypes(r.rawText, workTypes, matchedCodes, MAX_CANDIDATES)).slice(0, MAX_CANDIDATES)

  const th = 'px-3 py-2 text-left text-caption font-semibold text-text-muted'
  const td = 'border-b border-dashed border-border px-3 py-2.5 align-middle text-body-sm'
  // 공사일수 셀 — Counter(≈104px)+"일" 이 열 밖으로 넘치지 않게 좁은 패딩·가운데 정렬·줄바꿈 금지
  const tdDur = 'border-b border-dashed border-border px-2 py-2.5 align-middle'
  const counter = (code: string, value: number) => (
    <div className="flex items-center justify-center gap-1 whitespace-nowrap">
      <Counter value={value} onChange={v => setDuration(code, v)} min={1} size="sm" />
      <span className="text-caption text-text-muted">일</span>
    </div>
  )

  return (
    <div className="flex min-h-full flex-col bg-surface px-5 py-6">
      <div className="w-full">
        {/* 자동 생성의 마지막 입력 단계 = 공종/공사일 선택(F1 이동 규칙 §5) — 초기화 흐름만 2단계 표시. 견적서 흐름이라 앞 단계 이름은 '견적서 업로드' */}
        <StepIndicator step={resetSiteId ? 1 : 2} resetMode={!!resetSiteId} quote />
        <div className="mb-1 text-heading-sm text-text">추출 결과 확인</div>
        <div className="mb-4 text-caption text-text-muted">견적서에서 읽은 항목이에요. 공사 기간을 고르고, 연결이 틀렸거나 확인이 필요한 항목은 바로잡은 뒤 공사일수를 맞춰 주세요</div>

        {!raw ? (
          <div className="rounded-xl border border-border bg-surface-raised p-6 text-body-sm text-text-muted">
            추출 결과가 없습니다.
          </div>
        ) : (
          // 왼쪽: 공종표 영역 / 오른쪽: 견적서 원문 보조 패널(원문 보기를 켰을 때) — 좌우로 나란히 놓고 비교한다
          <div className="flex items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {raw.analysisStatus === 'FALLBACK_KEYWORD' && (
              <Alert kind="warning" role="alert">AI 분석에 실패해 키워드 기준으로 찾았어요 — 결과를 꼭 확인해 주세요</Alert>
            )}

            {/* 공사 기간 — 상단 고정(스크롤해도 표 위에 남는다, z-sticky 토큰). 검색 필터와 같은 기간 선택기, 일수는 토·일요일·공휴일을 뺀 작업일 수.
                오른쪽에 '선택한 공종 합계 / 공사기간'을 한 덩어리로 보여 주고, 합계가 기간보다 길 때만 합계 숫자를 위험색 + 초과 일수 글자로 표시한다(색만으로 전달하지 않는다, D-07 §3.2 #5) */}
            <section className="sticky top-0 z-sticky rounded-md border border-border bg-surface-raised px-5 py-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="text-label text-text">공사 기간</div>
                <DateRangePicker
                  value={period}
                  onChange={p => onChoice({ ...choice, period: p })}
                  presets={false}
                  holidays={holidayState.dates}
                  placeholder="기간 선택"
                  aria-label="공사 기간 선택"
                />
                {period.start && period.end
                  ? <StatusHint tone="info" icon={<IconInfoCircle width={16} height={16} />}>토·일요일, 공휴일은 제외한 일수예요</StatusHint>
                  : <StatusHint tone="muted" icon={<IconInfoCircle width={16} height={16} />}>공사기간을 선택해주세요</StatusHint>}
                <div className="ml-auto flex flex-wrap items-baseline gap-x-2 text-body-sm text-text-muted">
                  <span>선택한 공종 합계</span>
                  <b className={`text-heading-sm tabular-nums ${over != null && over > 0 ? 'text-danger' : 'text-group'}`}>{totalWorkDays}일</b>
                  {period.start && period.end && holidayState.status !== 'error' && (
                    periodDays != null
                      ? <span>/ 공사기간 {periodDays}일</span>
                      : <Skeleton variant="text" width={96} height={16} role="status" aria-busy="true" aria-label="공사 기간 일수 계산 중" />
                  )}
                  {over != null && over > 0 && (
                    <span className="inline-flex items-center gap-1 text-caption text-danger">
                      <IconWarningCircle width={16} height={16} aria-hidden="true" />{over}일 초과
                    </span>
                  )}
                </div>
                {period.start && period.end && holidayState.status === 'error' && (
                  <div className="basis-full">
                    <Alert kind="error" action={<Button variant="etc" size="sm" onClick={holidayState.retry}>다시 시도</Button>}>
                      <span>공휴일 정보를 불러오지 못해 일수를 계산할 수 없어요</span>
                    </Alert>
                  </div>
                )}
              </div>
            </section>

            {/* 공종표 — 견적서에서 읽은 내용과 표준 공종을 나란히 */}
            <section className="rounded-md border border-border bg-surface-raised shadow-sm">
              <div className="mx-5 mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 border-b-2 border-text pb-2.5">
                <div className="text-heading-sm tracking-wide text-text">공종표</div>
                <div className="text-caption text-text-muted">추출 표준 공종 {extractedCount}개{raw.sheetName ? ` · 분석 시트 ${raw.sheetName}` : ''}</div>
                {/* 보기 칩과 원문 보기 — 제목 오른쪽 */}
                <div className="ml-auto flex flex-wrap items-center gap-2" role="group" aria-label="공종표 보기">
                  <FilterChip active={filter === 'all'} aria-pressed={filter === 'all'} count={itemRows.length} onClick={() => setFilter('all')}>전체</FilterChip>
                  <FilterChip active={filter === 'open'} aria-pressed={filter === 'open'} count={unresolved} onClick={() => setFilter('open')}>확인 필요</FilterChip>
                  <FilterChip active={filter === 'skip'} aria-pressed={filter === 'skip'} count={excludedCount} onClick={() => setFilter('skip')}>제외한 항목</FilterChip>
                  {sourceFile && (
                    <Button variant="etc" size="sm" aria-expanded={sourceOpen} onClick={() => setSourceOpen(v => !v)}>
                      {sourceOpen ? '원문 닫기' : '원문 보기'}
                    </Button>
                  )}
                </div>
              </div>

              {/* 확인 필요(미해결 미매칭) 안내 — 모두 처리하면 사라진다 */}
              {unresolved > 0 && (
                <div className="mx-5 mt-3">
                  <Alert
                    kind="warning"
                    role="status"
                    action={<Button variant="etc" size="sm" onClick={jumpToNextOpen}>다음 확인 필요 항목</Button>}
                  >
                    <span>확인 필요 {unresolved}건 · 전체 미매칭 {unmatchedRows.length}건 중 {unmatchedRows.length - unresolved}건 처리했어요. 공종을 고르면 시공포함이 체크돼요 — 고르지 않은 항목은 스케줄에 들어가지 않아요</span>
                  </Alert>
                </div>
              )}

              {/* AI 표시(방법 A) — 헤더 한 번 + 설명 한 줄, 직접 바꾼 행에만 '수정' */}
              <div className="mx-5 mt-3">
                <StatusHint tone="info" icon={<IconInfoCircle width={16} height={16} />}>
                  AI 추출 — 견적서에서 AI가 찾아 표준 공종에 연결한 값이에요. 직접 바꾼 행에는 '수정'이 표시돼요
                </StatusHint>
              </div>

              <table ref={tableRef} className="mx-5 mb-0 mt-2 w-[calc(100%-2.5rem)] table-fixed border-collapse">
                <colgroup>
                  {/* 열 폭은 간격 토큰 클래스로 — w-11=80(시공포함 글자), w-07=32(→), w-13=160(일수 입력+'일') */}
                  <col className="w-11" />
                  <col />
                  <col className="w-07" />
                  <col style={{ width: '32%' }} />
                  <col className="w-13" />
                </colgroup>
                <thead>
                  <tr className="border-b border-border">
                    <th className={`${th} whitespace-nowrap px-1 text-center`}>시공포함</th>
                    <th className={th}>견적서에서 읽은 내용</th>
                    <th className={th}><span className="sr-only">연결</span></th>
                    <th className={th}>표준 공종 <Badge status="info" className="ml-1 align-middle">AI 추출</Badge></th>
                    <th className={`${th} text-center`}>공사일수</th>
                  </tr>
                </thead>
                <tbody>
                  {/* 빈 상태 — 표준 EmptyState(D-07 §14.2·D-09 §15.37). 필터 결과 없음은 필터를 푸는 행동을 함께 준다 */}
                  {shownRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6">
                        {filter === 'all' ? (
                          <EmptyState icon={<IconGuide />} title="표시할 항목이 없어요" description="견적서에서 읽은 항목이 없어요" />
                        ) : (
                          <EmptyState
                            icon={<IconGuide />}
                            title="해당하는 항목이 없어요"
                            description="다른 보기를 선택하면 나머지 항목을 볼 수 있어요"
                            action={<Button variant="secondary" size="sm" onClick={() => setFilter('all')}>전체 보기</Button>}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                  {shownRows.map(r => {
                    // 묶음 제목 행(예: 기타공사·세분화된 묶음) — 공종이 아니라 소속 이름만 보여 준다
                    if (r.header) {
                      return (
                        <tr key={r.key} className="bg-surface">
                          <td className={td} />
                          <td colSpan={4} className={`${td} font-semibold text-text`}>{r.rawText}</td>
                        </tr>
                      )
                    }
                    const child = r.depth === 1
                    const code = codeOf(r)
                    const excluded = isExcluded(r)
                    const hi = needsAction(r)                                         // 확인 필요(공종이 아직 없음) — 노랗게 강조
                    const included = isIncluded(r)
                    const codeName = byCode.get(code ?? '')?.workTypeName ?? r.work.matchedName
                    // 미매칭 행의 추천 공종 — 확인 필요일 때는 칩으로, 드롭다운 목록에서는 '추천' 표시로 보여 준다
                    const cands = r.matched ? [] : candidatesOf(r)
                    const candCodes = new Set(cands.map(c => c.workTypeCode))
                    return (
                      <tr key={r.key} data-open={hi ? 'true' : undefined} style={hi ? WARN_BG : undefined}>
                        {/* 시공포함 체크박스 — 체크된 행만 스케줄로 만든다. 공종이 있는 행은 기본 체크이고 해제하면 드롭다운·일수가 잠긴다.
                            공종이 아직 없는 행은 체크 안 된 채 잠겨 있다가 공종을 고르면 자동으로 체크된다 */}
                        <td className={`${td} text-center`} style={hi ? WARN_EDGE : undefined}>
                          <Checkbox bare checked={included} disabled={!code} onChange={e => toggleOff(r.key, e.target.checked)} aria-label={`${r.rawText} 시공포함`} />
                        </td>
                        {/* 견적서에서 읽은 내용 — 공사 대분류 오른쪽에 상세 내용을 이어 한 줄로(길면 말줄임, 마우스를 올리면 전체).
                            시공포함을 해제한 행은 opacity 대신 보조 글자색 토큰으로 낮춘다(글자 대비 유지, 체크박스는 그대로 보여 다시 켤 수 있다) */}
                        <td className={td}>
                          <div className={`flex min-w-0 items-baseline gap-2 ${child ? 'pl-4' : ''}`}>
                            {child && <span className="text-text-muted" aria-hidden="true">└</span>}
                            <span className={`truncate ${excluded ? 'text-text-muted' : 'text-text'} ${hi ? 'font-semibold' : ''}`} title={r.rawText}>{r.rawText || '—'}</span>
                            {r.work.detail && <span className="min-w-0 flex-1 truncate text-caption text-text-muted" title={r.work.detail}>{r.work.detail}</span>}
                            {/* 해야 할 일이 있는 행에만 표시 — 색(노란 줄)만으로 전달하지 않도록 글자로도 알린다 */}
                            {hi && <Badge status="warning" className="ml-auto shrink-0">확인 필요</Badge>}
                          </div>
                        </td>
                        <td className={`${td} px-0 text-center text-text-muted`} aria-hidden="true">→</td>
                        {/* 표준 공종 — 모든 행이 드롭다운. AI 추출이라는 점은 헤더에서 한 번 알리고, 사용자가 바꾼 행에만 '수정'을 붙인다 */}
                        <td className={td}>
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 flex-1">
                          <Combobox
                            size="sm"
                            searchable
                            value={code ?? ''}
                            displayValue={codeName ?? undefined}
                            placeholder="공종을 골라 주세요"
                            // 시공포함을 해제한 행은 잠근다 — 뺄지 말지는 시공포함 체크로만 관리한다
                            disabled={excluded}
                            options={pickOptions}
                            emptyText="검색 결과가 없어요"
                            aria-label={`'${r.rawText}'에 연결할 공종`}
                            renderOption={o => {
                              // 고르는 순간에만 필요한 정보를 목록 안에서 알린다 — AI 연결(되돌릴 항목)·추천·이미 포함됨(같은 공종 중복 → 소요기간을 함께 씀)
                              const tags = [
                                r.matched && o.value === r.work.matchedCode ? 'AI 연결' : null,
                                !r.matched && candCodes.has(o.value) ? '추천' : null,
                                usedByOther(o.value, included && o.value === code) ? '이미 포함됨' : null,
                              ].filter(Boolean).join(' · ')
                              // 목록 항목은 가로 flex — 이름 칸(menu-label)이 남는 폭을 차지해 표시가 오른쪽 끝에 놓인다
                              return <><span className="menu-label">{o.label}</span>{tags && <span className="text-caption text-text-muted">{tags}</span>}</>
                            }}
                            // AI 가 연결한 공종을 그대로 고르면 사용자 선택을 풀어 자동 연결로 되돌린다
                            onChange={v => setPick(r.key, r.matched && v === r.work.matchedCode ? null : v)}
                          />
                            </div>
                            {choice.picks[r.key] && <Badge status="mute" className="shrink-0">수정</Badge>}
                          </div>
                          {/* 추천 공종 — 한 번에 연결(최대 MAX_CANDIDATES 개) */}
                          {hi && cands.length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <span className="text-caption text-text-muted">추천</span>
                              {cands.map(c => (
                                <Button key={c.workTypeCode} variant="secondary" size="sm" aria-label={`'${r.rawText}'를 ${c.workTypeName}에 연결`} onClick={() => setPick(r.key, c.workTypeCode)}>
                                  {c.workTypeName}
                                </Button>
                              ))}
                            </div>
                          )}
                        </td>
                        {/* 공사일수 */}
                        <td className={tdDur}>
                          {!code || excluded
                            ? <div className="text-center text-text-muted">—</div>
                            : durationOwner.get(code) !== r.key
                              ? <div className="text-center text-caption text-text-muted">위 행과 함께</div>
                              : counter(code, durationOf(code, extractedDuration(r, code)))}
                        </td>
                      </tr>
                    )
                  })}

                  {/* 직접 추가한 공종 — 견적서에 없지만 스케줄에 넣을 공종. 항상 포함이라 체크 대신 행 삭제 */}
                  {filter === 'all' && (
                    <>
                      <tr className="bg-surface">
                        <td colSpan={5} className="border-b border-border px-3 py-1.5 text-caption font-semibold text-text-muted">
                          견적서에 없지만 스케줄에 넣는 공종
                        </td>
                      </tr>
                      {choice.added.map(code => (
                        <tr key={code}>
                          <td className={`${td} text-center`}>
                            <Button variant="etc" size="sm" aria-label={`${byCode.get(code)?.workTypeName ?? '추가한 공종'} 행 삭제`} onClick={() => toggleAdded(code)}>삭제</Button>
                          </td>
                          <td className={td}><Badge status="info">직접 추가</Badge></td>
                          <td className={`${td} px-0 text-center text-text-muted`} aria-hidden="true">→</td>
                          <td className={td}><span className="font-semibold text-text">{byCode.get(code)?.workTypeName ?? code}</span></td>
                          <td className={tdDur}>
                            {durationOwner.get(code) !== `add:${code}`
                              ? <div className="text-center text-caption text-text-muted">위 행과 함께</div>
                              : counter(code, durationOf(code))}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={5} className="px-3 py-2.5">
                          <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>+ 공종 추가</Button>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>

              {/* 공통 항목 — 견적서에는 없지만 스케줄에 함께 들어가는 행정 공정(스케줄 전용). 기본은 접어 두되 합계에는 포함한다 */}
              {commons.length > 0 && filter === 'all' && (
                <CollapsibleSection
                  className="mx-5 mb-5 mt-2"
                  title="공통 항목"
                  notice={
                    <StatusHint tone="muted" icon={<IconInfoCircle width={16} height={16} />}>
                      견적서에는 없지만 스케줄에 함께 들어가는 행정 공정 · {checkedCommons.length}개 · {commonDays}일 포함
                    </StatusHint>
                  }
                  open={commonsOpen}
                  onToggle={() => setCommonsOpen(v => !v)}
                >
                  <ul className="flex flex-col">
                    {commons.map(c => {
                      const on = !choice.commonOff.includes(c.workTypeCode)
                      return (
                        <li key={c.workTypeCode} className={`flex items-center gap-3 border-b border-dashed border-border px-3 py-2.5 text-body-sm`}>
                          <Checkbox bare checked={on} onChange={e => toggleCommon(c.workTypeCode, e.target.checked)} aria-label={`${c.workTypeName} 포함`} />
                          <span className={`min-w-0 flex-1 truncate ${on ? 'font-semibold text-text' : 'text-text-muted'}`}>{c.workTypeName}</span>
                          {on ? counter(c.workTypeCode, durationOf(c.workTypeCode)) : <span className="text-text-muted">—</span>}
                        </li>
                      )
                    })}
                  </ul>
                </CollapsibleSection>
              )}
              {(commons.length === 0 || filter !== 'all') && <div className="mb-5" />}
            </section>

            {/* 이번에 바꾼 소요일을 앞으로의 기본 소요일로 저장(바꾼 소요일이 있을 때만 · 기본 해제) */}
            {changedDurations.length > 0 && (
              <Checkbox
                checked={choice.saveDefaults}
                onChange={e => onChoice({ ...choice, saveDefaults: e.target.checked })}
                label="이번에 바꾼 소요일을 앞으로의 기본 소요일로 저장"
              />
            )}

            {/* 원본 응답 — 필요할 때만 펼쳐 본다 */}
            <details className="rounded-xl border border-border bg-surface-raised p-4">
              <summary className="cursor-pointer text-body-sm font-bold text-text">원본 응답 보기 (source: {raw.source})</summary>
              <pre className="mt-2 max-h-[320px] overflow-auto whitespace-pre-wrap break-all rounded-md bg-surface p-3 text-caption text-text-muted">
                {raw.rawAnswer || '(없음)'}
              </pre>
            </details>
          </div>

          {/* 견적서 원문 — 표준 SidePanel(비모달)이라 공종표와 좌우로 나란히 보면서 바로 고칠 수 있다. 화면을 스크롤해도 같이 따라온다(sticky).
              폭 480 은 문서를 읽을 최소 폭이다(SidePanel 기본 308 은 보조 패널용) — UI팀 확인 필요. 높이는 화면 높이에서 상단바를 뺀 값 */}
          {sourceFile && sourceOpen && (
            <div className="sticky top-0 self-start">
              <SidePanel
                open
                width={480}
                style={{ height: 'calc(100vh - var(--layout-topbar-height))' }}
                title={
                  <>
                    <span className="min-w-0 truncate" title={sourceFile.name}>견적서 원문 · {sourceFile.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => setSourceOpen(false)}>닫기</Button>
                  </>
                }
              >
                <Temp_SourceViewer file={sourceFile} />
              </SidePanel>
            </div>
          )}
          </div>
        )}

        {raw && !canCreate && (
          <div className="mt-4">
            <StatusHint tone="muted" icon={<IconInfoCircle width={16} height={16} />}>{createBlockedReason}</StatusHint>
          </div>
        )}
        <div className="mt-4 flex gap-2.5 border-t border-border pt-4">
          <Button variant="secondary" size="lg" onClick={() => navigate('create-upload2')}>
            이전
          </Button>
          <Button variant="primary" size="lg" className="flex-1" onClick={handleCreate} disabled={!raw || !canCreate || busy} loading={busy}>
            스케줄 생성하기
          </Button>
        </div>
      </div>

      {/* 공종 추가 — 견적서에 없는 공종을 칩으로 골라 한 번에 여러 개 넣는다(다시 누르면 뺀다) */}
      <Modal
        open={addOpen}
        onClose={closeAdd}
        title="공종 추가"
        size="lg"
        footer={<Button variant="primary" onClick={closeAdd}>완료</Button>}
      >
        <Search autoFocus value={addQuery} onChange={setAddQuery} placeholder="공종 이름 검색" />
        <p className="mt-2 text-caption text-text-muted">견적서에 없는 공종을 눌러 추가해 주세요. 한 번 더 누르면 빠져요. 이미 들어간 공종은 흐리게 보여요</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {addList.length === 0 && <span className="py-4 text-body-sm text-text-muted">검색 결과가 없어요</span>}
          {addList.map(w => {
            const on = choice.added.includes(w.workTypeCode)
            return (
              <FilterChip
                key={w.workTypeCode}
                active={on}
                aria-pressed={on}
                disabled={!on && usedByOther(w.workTypeCode, false)}
                onClick={() => toggleAdded(w.workTypeCode)}
              >
                {w.workTypeName}
              </FilterChip>
            )
          })}
        </div>
      </Modal>

      {/* 공사 기간 초과 확인 — 공종 합계가 공사 기간보다 길어도 막지는 않고 한 번 더 묻는다 */}
      <Modal
        open={showOverrunConfirm}
        onClose={() => setShowOverrunConfirm(false)}
        title="공사 기간 초과"
        footer={<>
          <Button variant="secondary" onClick={() => setShowOverrunConfirm(false)}>취소</Button>
          <Button variant="primary" onClick={() => { setShowOverrunConfirm(false); void doSubmit() }}>진행</Button>
        </>}
      >
        <Alert kind="warning">
          <span>선택한 공종 합계({totalWorkDays}일)가 공사 기간({periodDays}일)보다 깁니다. 그래도 진행하시겠습니까?</span>
        </Alert>
      </Modal>

      {/* 이미 스케줄이 있는 현장 확인 다이얼로그 */}
      <ScheduleReplaceModal open={replaceOpen} onCancel={cancelReplace} onConfirm={confirmReplace} existingScheduleId={replaceScheduleId} />
    </div>
  )
}
